import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ShowItem } from '@/mocks/shows';

interface Props {
  items: ShowItem[];
}

export default function HeroCarousel({ items }: Props) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setActive((v) => (v + 1) % items.length);
    }, 6000);
    return () => clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;
  const current = items[active];

  return (
    <section className="relative w-full h-[480px] md:h-[600px] overflow-hidden">
      {items.map((item, idx) => (
        <div
          key={item.id}
          className={`absolute inset-0 transition-opacity duration-700 ${idx === active ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
        >
          <img src={item.cover} alt={item.title} className="w-full h-full object-cover object-top" />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/50 to-primary-950/20"></div>
        </div>
      ))}

      <div className="relative z-10 h-full w-full flex items-end">
        <div className="mx-auto w-full max-w-[1440px] px-4 md:px-8 pb-10 md:pb-16">
          <span className="inline-flex items-center gap-1.5 bg-accent-500 text-background-50 text-xs font-label font-bold px-3 py-1 rounded-full whitespace-nowrap">
            <i className="ri-fire-fill"></i>
            焦点热播
          </span>
          <h1 className="mt-4 font-heading text-3xl md:text-5xl font-black text-background-50 max-w-2xl leading-tight">
            {current.title}
          </h1>
          <p className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-background-200 font-label">
            <span>{current.year}</span>
            <span>·</span>
            <span>{current.region}</span>
            <span>·</span>
            <span>{current.genres.join(' / ')}</span>
            <span className="flex items-center gap-1 text-accent-400">
              <i className="ri-star-fill"></i>
              {current.rating}
            </span>
          </p>
          <p className="mt-4 max-w-xl text-sm md:text-base text-background-100 font-body line-clamp-2">
            {current.summary}
          </p>
          <div className="mt-6 flex items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="inline-flex items-center gap-2 bg-accent-500 hover:bg-accent-600 text-background-50 font-label font-bold text-sm px-6 py-3 rounded-md transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-information-line"></i>
              查看剧情详情
            </Link>
            <div className="hidden sm:flex items-center gap-2">
              {items.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActive(idx)}
                  aria-label={`切换到第${idx + 1}张`}
                  className={`h-1.5 rounded-full transition-all cursor-pointer ${
                    idx === active ? 'w-8 bg-accent-500' : 'w-4 bg-background-50/40'
                  }`}
                ></button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}